<template>
  <div class="w-full mb-10">
    <h1 class="text-grey-darker text-xl leading-normal mb-3">
      Availability Calendar
    </h1>

    <div class="max-w-3xl rounded p-4 bg-white w-full h-full">
      <div class="w-full flex justify-between items-center border-b mb-10"> <!--Navigator -->
        <div class="flex" style="margin-bottom: -.90rem;">
          <button
            class="p-5 rounded-tl border border focus:outline-none"
            @click="type = types.CAL_TYPE_VALUATION"
            :class="{
              'text-grey bg-grey-light': type !== types.CAL_TYPE_VALUATION,
              'bg-white text-grey-darkest border-b-0': type === types.CAL_TYPE_VALUATION
            }"
          >
          Valuation Hours
          </button>
          <button
            class="p-5 rounded-tr border border border-b-0 border-l-0 focus:outline-none"
            @click="type = types.CAL_TYPE_VIEWING"
            :class="{
              'text-grey bg-grey-light': type !== types.CAL_TYPE_VIEWING,
              'bg-white text-grey-darkest': type === types.CAL_TYPE_VIEWING
            }"
          >
          Viewing Hours
          </button>
        </div>
        <div class="flex items-center p-3">
          <div class="flex items-center ">
            <button
              @click="weekOffset = 0"
              class="h-12 flex items-center justify-center border rounded p-2 mr-6"
            >
              Go to this week
            </button>
            <button
              @click="decrementWeek"
              class="w-12 h-12 flex items-center justify-center border rounded p-2 mr-6"
            >
              <i class="fas text-3xl fa-chevron-left text-grey-darkest"></i>
            </button>

            <div class="w-70 text-center mr-6 text-grey-darkest">
              <span class="font-bold">{{ currentWeek.format('dddd DD MMMM') }}</span>
              to
              <span class="font-bold">{{ currentWeek.clone().endOf('isoWeek').format('dddd DD MMMM') }}</span>
            </div>

            <button
              @click="incrementWeek"
              class="w-12 h-12 flex items-center justify-center border rounded p-2"
            >
              <i class="fas text-3xl fa-chevron-right text-grey-darkest"></i>
            </button>
          </div>
        </div>
      </div> <!-- navigator end -->

      <div class="w-full flex justify-center mb-10">
        <div class="flex items-center border-r pr-8 mr-4">
          <icon class="w-12 h-12 mr-4" name="finger_drag" :center="false"></icon>
          <p><span class="font-bold">Click and drag</span> to create or amend time blocks</p>
        </div>

        <div class="flex items-center">
          <icon class="w-12 h-12 mr-4" name="finger_click" :center="false"></icon>
          <p><span class="font-bold">Right click</span> on a block to repeat or delete a time</p>
        </div>
      </div>

      <div class="calendar-container h-full w-full flex overflow-x-scroll overflow-y-hidden">
        <div>
          <div class="flex w-full pl-24 mb-2">
            <span
              v-for="hour in 12"
              :key="`hour-${hour}`"
              class="font-bold select-none"
              :style="{
                width: (blockSize + marginRight) * 4 + 'px'
              }"
              v-html="formatHours(hour - 1)"
            />
          </div>

          <div class="flex w-full pl-24">
            <div
              v-for="hour in 12"
              class="relative"
              :key="`marker-${hour}`"
              :style="{
                width: (blockSize + marginRight) * 4 + 'px'
              }"
            >
              <div
                v-if="hour < 12"
                class="pin-r border-l border-dashed border-grey-light absolute"
                :style="{
                  right: '4px',
                  height: markerHeight + 'px'
                }"
              />
            </div>
          </div>

          <availability-context-menu
            v-if="contextMenuOpen"
            :time="contextMenuTime"
            :event="contextMenuEvent"
            :weekOffset="weekOffset"
          />

          <div v-for="(day, index) in days" :key="index" class="w-full flex flex-col mb-3">
            <div class="flex w-full">
              <div class="w-20 flex-no-shrink h-10 bg-grey-lighter rounded text-center flex flex-col items-center justify-center mb-4 mr-4 select-none">
                <span class="font-bold">{{ day.weekday }}</span>
                <span class="text-xs">{{ day.dateMonth }}</span>
              </div>
              <div class="flex relative select-none">
                  <availability-block
                    v-for="(time, index) in availabilityTimes[day.timesKey]"
                    :key="`availability-block-${index}`"
                    :block-size="blockSize"
                    :offset="marginRight"
                    :calendarType="type"
                    :weekOffset="weekOffset"
                    :time="time"
                    @contextmenu.native.prevent="openContextMenu($event, time)"
                  />
                <div
                  v-for="t in 48" :key="t"
                  class="block"
                  :class="{
                    'rounded-r' : t === 49,
                    'rounded-l' : t === 1,
                  }"
                  :style="{
                    width: blockSize + 'px',
                    marginRight: marginRight + 'px'
                  }"
                  @mousedown="createNewTime(day.timesKey, t)"
                >
                  <span class="text-grey">+</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import AvailabilityBlock from '@/components/AvailabilityBlock'
import AvailabilityContextMenu from '@/components/AvailabilityContextMenu'
import { mapState, mapActions } from 'vuex'

export default {
  name: 'AvailabilityCalendar',

  components: {
    AvailabilityBlock,
    AvailabilityContextMenu
  },

  data () {
    return {
      blockSize: 15,
      weekOffset: 0,
      week: moment().startOf('isoWeek'),
      markerHeight: 0,
      types: {
        CAL_TYPE_VIEWING: 0,
        CAL_TYPE_VALUATION: 1,
        TIME_TYPE_UNSET: 0,
        TIME_TYPE_SINGLE: 1,
        TIME_TYPE_REPEATED: 2
      },
      type: 1,
      marginRight: 9,
      loading: false,
      contextMenuOpen: false,
      contextMenuEvent: {},
      contextMenuTime: {}
    }
  },

  created () {
    document.addEventListener('click', this.closeContextMenu)
  },

  beforeDestroy () {
    document.removeEventListener('click', this.closeContextMenu)
  },

  mounted () {
    this.markerHeight = this.$el
      .querySelector('.calendar-container')
      .getBoundingClientRect()
      .height - 30

    this.getAvailabilityTimes({
      type: this.type,
      weekOffset: this.weekOffset
    })
  },

  computed: {
    ...mapState([
      'selectedOffice',
      'availabilityTimes'
    ]),

    currentWeek () {
      return this.week.clone().add(this.weekOffset, 'weeks')
    },

    days () {
      let week = this.currentWeek.clone()
      let days = []
      for (let i = 0; i < 7; i++) {
        days.push({
          timesKey: week.format('ddd').toLowerCase(),
          weekday: week.format('ddd'),
          dateMonth: week.format('DD/MM')
        })
        week.add(1, 'day')
      }
      return days
    }
  },

  methods: {
    ...mapActions([
      'getAvailabilityTimes',
      'addAvailabilityTime',
      'setAvailabilityTimes'
    ]),

    openContextMenu (event, time) {
      this.contextMenuOpen = true
      this.contextMenuEvent = event
      this.contextMenuTime = time
    },

    closeContextMenu () {
      this.contextMenuOpen = false
      this.contextMenuEvent = {}
      this.contextMenuTime = {}
    },

    createNewTime (weekday, timeIndex) {
      let datetime = moment().set('hour', 8).set('minutes', (timeIndex - 1) * 15)
      let time = {
        id: null,
        weekday: weekday,
        start: { hour: datetime.get('hour'), minute: datetime.get('minutes') },
        end: { hour: datetime.get('hour'), minute: datetime.get('minutes') + 15 }
      }

      this.addAvailabilityTime(time)
    },

    incrementWeek () {
      this.weekOffset++
    },

    decrementWeek () {
      this.weekOffset--
    },

    formatHours (hours) {
      return moment().set('hour', 8).add(hours, 'hours').format('hA')
    }
  },

  watch: {
    selectedOffice () {
      this.getAvailabilityTimes({
        type: this.type,
        weekOffset: this.weekOffset
      })
    },

    type () {
      this.getAvailabilityTimes({
        type: this.type,
        weekOffset: this.weekOffset
      })
    },

    currentWeek () {
      this.getAvailabilityTimes({
        type: this.type,
        weekOffset: this.weekOffset
      })
    }
  }
}
</script>

<style scoped>
.block {
  @apply h-10 bg-grey-lighter relative flex items-center justify-center flex-no-shrink select-none cursor-pointer
}

.block > span:first-child {
  display: none;
}

.block:hover > span {
  display: block
}
</style>
